import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Switch,
  Menu,
  MenuItem,
  Alert,
  // Button,  // Not used
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  // Security as SecurityIcon,  // Not used
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

/**
 * PolicyAssignmentList Component
 *
 * Displays a list of policy assignments for a template version.
 * Shows policy details, action on violation, enabled status, and actions.
 */
const PolicyAssignmentList = ({
  assignments,
  onEdit,
  onDelete,
  onToggle,
  onViewHistory,
  loading,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const handleMenuOpen = (event, assignment) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedAssignment(assignment);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedAssignment(null);
  };

  const getActionColor = action => {
    switch (action) {
      case 'log':
        return 'default';
      case 'warn':
        return 'warning';
      case 'anonymize':
        return 'info';
      case 'block':
        return 'error';
      default:
        return 'default';
    }
  };

  const getDomainColor = domain => {
    const colors = {
      pii: 'primary',
      hipaa: 'success',
      financial: 'warning',
      security: 'error',
      compliance: 'info',
    };
    return colors[domain.toLowerCase()] || 'default';
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Loading assignments...
        </Typography>
      </Paper>
    );
  }

  if (!assignments || assignments.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info">
          <Typography variant="body1" gutterBottom>
            No policies assigned
          </Typography>
          <Typography variant="body2">
            Click "Assign Policy" to add a content check policy to this template
            version.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Policy Name</TableCell>
            <TableCell>Domains</TableCell>
            <TableCell>Action on Violation</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {assignments.map(assignment => (
            <TableRow key={assignment.assignment_id || assignment.id}>
              <TableCell>
                <Typography variant="body1" fontWeight={500}>
                  {assignment.policy_name || 'Unknown Policy'}
                </Typography>
                {assignment.policy_id && (
                  <Typography variant="caption" color="text.secondary">
                    ID: {assignment.policy_id.slice(0, 8)}...
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {assignment.policy_domains &&
                assignment.policy_domains.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {assignment.policy_domains.map(domain => (
                      <Chip
                        key={domain}
                        label={domain.toUpperCase()}
                        size="small"
                        color={getDomainColor(domain)}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No domains
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  label={assignment.action_on_violation || 'log'}
                  size="small"
                  color={getActionColor(assignment.action_on_violation)}
                  sx={{ textTransform: 'capitalize' }}
                />
              </TableCell>
              <TableCell>
                <Switch
                  checked={assignment.enabled !== false}
                  onChange={e => {
                    onToggle(
                      assignment.assignment_id || assignment.id,
                      e.target.checked
                    );
                  }}
                  size="small"
                />
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {assignment.enabled !== false ? 'Enabled' : 'Disabled'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box display="flex" justifyContent="flex-end" gap={0.5}>
                  <Tooltip title="Edit Assignment">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(assignment)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View History">
                    <IconButton
                      size="small"
                      onClick={() => onViewHistory(assignment)}
                      color="info"
                    >
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="More Options">
                    <IconButton
                      size="small"
                      onClick={e => handleMenuOpen(e, assignment)}
                      color="default"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedAssignment) {
              onDelete(
                selectedAssignment.assignment_id || selectedAssignment.id
              );
            }
            handleMenuClose();
          }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Remove Assignment
        </MenuItem>
      </Menu>
    </TableContainer>
  );
};

export default PolicyAssignmentList;
